import { useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";

/**
 * A side sheet. Every form that is not the page's own opens in one, so the page stays a list (the
 * sketches of 2026-09-24).
 *
 * **The browser's own `<dialog>`, opened with `showModal()`.** The first version used Radix Dialog,
 * which the portal carries, and the full browser suite failed on it at once: Radix locks the page's
 * scroll by injecting a `<style>` element, and the Worker's Content-Security-Policy allows no inline
 * style, so every sheet logged a violation and `tests/e2e/csp.ts` failed the test. A modal `<dialog>`
 * gives the three things a modal must have without any of that: the page behind it is inert, focus
 * stays inside, and Escape closes it. The fourth, putting focus back where it was, is done here,
 * because the sheet is unmounted rather than closed when the caller says so.
 *
 * The sheet stays open after its form succeeds, so the result line the form writes is read where the
 * act happened, and the person closes it. It renders nothing while closed, so a form inside it holds
 * no state and makes no read until it is opened.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  danger = false,
  testId,
  children
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  danger?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  if (!open) {
    return null;
  }

  return createPortal(
    <OpenSheet title={title} danger={danger} testId={testId} onClose={() => onOpenChange(false)}>
      {children}
    </OpenSheet>,
    document.body
  );
}

function OpenSheet({
  title,
  danger,
  testId,
  onClose,
  children
}: {
  title: string;
  danger: boolean;
  testId?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  /*
   * Opened before paint, so the first frame the person sees is the sheet and not the page flashing.
   *
   * Focus goes back to whatever opened the sheet, a row's menu button or a page's primary act. The
   * opener is read here, before `showModal()` moves focus into the sheet. Until 2026-09-25 it was read
   * in a `useEffect`, which runs after this, so what it remembered was the sheet's own close button,
   * and focus fell to the page's body when the sheet went (found by the builder of the client's
   * Ärenden in the second build round). On the way out the dialog is closed first, because while it is
   * modal the page behind it is inert and refuses focus.
   */
  useLayoutEffect(() => {
    const element = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (element && !element.open) {
      element.showModal();
    }
    return () => {
      if (element?.open) {
        element.close();
      }
      if (opener && opener !== document.body && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className={`sheet${danger ? " is-danger" : ""}`}
      aria-labelledby={titleId}
      data-testid={testId}
      onCancel={(event) => {
        // Escape. The caller unmounts the sheet, which is what closes it.
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A press on the backdrop lands on the dialog element itself, outside its content box.
        if (event.target === dialog.current) {
          onClose();
        }
      }}
    >
      <div className="sheet-frame">
        <header className="sheet-head">
          <h2 id={titleId}>{title}</h2>
          <button className="icon-button is-small" type="button" aria-label="Stäng" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="sheet-body">{children}</div>
      </div>
    </dialog>
  );
}
